import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * A deliberately small Markdown renderer for manual texts: headings, paragraphs, bullet and
 * numbered lists (nested by indentation), **bold**, *italic*, `code` and links.
 *
 * It builds React elements and never uses innerHTML, so raw HTML in the text is shown as plain
 * text and cannot run. Links are limited to http(s), mailto, in-app paths (/...) and #anchors.
 */

const SAFE_URL = /^(https?:\/\/|mailto:|\/(?![/\\])|#)/i;
export const isSafeUrl = (url: string): boolean => SAFE_URL.test(url.trim());

/** In-app targets (`/path`, `#anchor`) go through the router. */
const isInternal = (url: string): boolean => url.startsWith('/') || url.startsWith('#');

const INLINE = /(`[^`\n]+`)|(\*\*[^*\n]+?\*\*)|(\*[^*\s][^*\n]*?\*)|(\[[^\]\n]+\]\([^)\s]+\))/;

function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let n = 0;
  while (rest.length > 0) {
    const m = INLINE.exec(rest);
    if (!m) {
      out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const token = m[0];
    const key = `${keyPrefix}-${n++}`;
    if (m[1]) out.push(<code key={key}>{token.slice(1, -1)}</code>);
    else if (m[2]) out.push(<strong key={key}>{inline(token.slice(2, -2), key)}</strong>);
    else if (m[3]) out.push(<em key={key}>{inline(token.slice(1, -1), key)}</em>);
    else {
      const close = token.indexOf('](');
      const label = token.slice(1, close);
      const url = token.slice(close + 2, -1);
      if (!isSafeUrl(url)) out.push(<Fragment key={key}>{label}</Fragment>);
      else if (isInternal(url)) out.push(<Link key={key} to={url}>{inline(label, key)}</Link>); // router: no full reload
      else out.push(<a key={key} href={url}>{inline(label, key)}</a>);
    }
    rest = rest.slice(m.index + token.length);
  }
  return out;
}

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: { text: string; children: Block[] }[] };

const HEADING = /^(#{1,6})\s+(.*)$/;
const LIST_ITEM = /^(\s*)([-*]|\d+[.)])\s+(.*)$/;

function parse(lines: string[]): Block[] {
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (line.trim() === '') {
      i++;
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      blocks.push({ kind: 'heading', level: (heading[1] ?? '#').length, text: (heading[2] ?? '').trim() });
      i++;
      continue;
    }
    const item = LIST_ITEM.exec(line);
    if (item) {
      const baseIndent = (item[1] ?? '').length;
      const ordered = /\d/.test(item[2] ?? '');
      const items: { text: string; children: Block[] }[] = [];
      while (i < lines.length) {
        const current = lines[i] ?? '';
        const m = LIST_ITEM.exec(current);
        if (!m || (m[1] ?? '').length < baseIndent) break;
        if ((m[1] ?? '').length === baseIndent) {
          items.push({ text: m[3] ?? '', children: [] });
          i++;
          continue;
        }
        // deeper indentation: belongs to the previous item as a nested list
        const nested: string[] = [];
        while (i < lines.length) {
          const l = lines[i] ?? '';
          const lm = LIST_ITEM.exec(l);
          if (!lm || (lm[1] ?? '').length <= baseIndent) break;
          nested.push(l);
          i++;
        }
        items[items.length - 1]?.children.push(...parse(nested));
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length) {
      const l = lines[i] ?? '';
      if (l.trim() === '' || HEADING.test(l) || LIST_ITEM.test(l)) break;
      para.push(l.trim());
      i++;
    }
    blocks.push({ kind: 'paragraph', text: para.join(' ') });
  }
  return blocks;
}

function renderBlocks(blocks: Block[], keyPrefix: string, headingOffset: number): ReactNode[] {
  return blocks.map((block, index) => {
    const key = `${keyPrefix}${index}`;
    if (block.kind === 'heading') {
      const Tag = `h${Math.min(6, block.level + headingOffset)}` as 'h1';
      return <Tag key={key}>{inline(block.text, key)}</Tag>;
    }
    if (block.kind === 'paragraph') return <p key={key}>{inline(block.text, key)}</p>;
    const List = block.ordered ? 'ol' : 'ul';
    return (
      <List key={key}>
        {block.items.map((item, j) => (
          <li key={`${key}-${j}`}>
            {inline(item.text, `${key}-${j}`)}
            {renderBlocks(item.children, `${key}-${j}-`, headingOffset)}
          </li>
        ))}
      </List>
    );
  });
}

/** `headingOffset` shifts heading levels (a `###` section inside a page whose title is `h2` can become `h3` as written, so 0 by default). */
export function Markdown({ text, headingOffset = 0 }: { text: string; headingOffset?: number }) {
  const blocks = parse(text.replaceAll('\r\n', '\n').split('\n'));
  return <div className="prose">{renderBlocks(blocks, 'md', headingOffset)}</div>;
}

/** The first heading of a text without its `#` signs (used as a section title); `undefined` when there is none. */
export function firstHeading(text: string): string | undefined {
  for (const line of text.split('\n')) {
    const m = HEADING.exec(line);
    if (m) return (m[2] ?? '').replace(/[*_`]/g, '').trim();
  }
  return undefined;
}
